'use client';

/**
 * ChargeIAP Component
 *
 * Headless processor for In-App Purchase (IAP) payments.
 * Delegates payment to the native mobile app via postMessage.
 *
 * Flow:
 * 1. Send `on_pay` message to native app with payment option data
 * 2. Native app handles IAP via Apple/Google IAP SDKs
 * 3. Listen for `on_pay_completed` message from native app
 * 4. Call onSuccess when completed
 *
 * Matches old flow behavior from purchase_details_for_mobile_app.js:
 * - forMobileBuyBtnOnClickHandler sends { action: 'on_pay', params: { ...option, purchaseFees } }
 * - Native app processes IAP and sends back 'on_pay_completed'
 */

import React, { useEffect } from 'react';
import { sendOnPay, messageToAppKeys } from '../../mobile/mobileMessaging';
import type { ChargeProcessorProps } from '../../types';

/** Safe JSON parse — returns parsed object or false on failure */
const tryParseJSONObject = (jsonString: string): Record<string, any> | false => {
  try {
    const o = JSON.parse(jsonString);
    if (o && typeof o === 'object') return o;
  } catch { /* ignore */ }
  return false;
};

export const ChargeIAP: React.FC<ChargeProcessorProps> = ({
  amount,
  paymentSourceId,
  purchaseType,
  onStatusChange,
  onSuccess,
  startSignal = 0
}) => {
  useEffect(() => {
    if (!startSignal) return undefined;

    // Report processing status
    onStatusChange?.('processing', 'Waiting for in-app purchase...');

    // Build params matching old flow's forMobileBuyBtnOnClickHandler format:
    // { ...option, purchaseFees }
    // where option = { kind: 'iap', humanKind: 'InApp', context: {}, id: ... }
    const params: Record<string, any> = {
      kind: 'iap',
      humanKind: 'InApp',
      context: {},
      id: paymentSourceId,
      purchaseFees: 0, // Fees are included in the amount
      amount,
      purchase_type: purchaseType
    };

    // Send on_pay message to native app
    sendOnPay(params);

    // Listen for on_pay_completed response from native app
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;

      const parsedMessage = tryParseJSONObject(e.data);
      if (!parsedMessage) return;

      const action = parsedMessage[messageToAppKeys.action];

      if (action === messageToAppKeys.onPayCompeted) {
        onSuccess({
          success: true
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [startSignal]);

  // Headless component — no UI rendered
  return null;
};
